import { httpClient } from 'api';
import { Session, SessionReflections } from 'types/Session';
import { SessionHomeworkPayload, SessionPayload, SessionRatingPayload } from 'types/payloads/SessionPayload';
import { ProgramMembership } from 'types/Membership';
import { CalendarMeetingPayload } from '../types/payloads/CalendarMeetingPayload';

type VideoCallRoom = {
	id: number;
	internalId: number;
	validFrom: Date;
	validUntil: Date;
	sessionMeetingId: number;
	createdAt: Date;
	updatedAt: Date;
};

type VideoCallToken = {
	token: string;
	expiresOn: Date;
	communicationUserId: string;
};

type SessionMeetingVideoCallResponse = {
	session: Session;
	room: VideoCallRoom | null;
	tokenData: VideoCallToken | null;
	agendaHtml?: string;
	userRole?: 'mentor' | 'mentee';
	currentMembership: ProgramMembership;
	prevSessionId: number | null;
	nextSessionId: number | null;
	contractDuration: number | null;
};

export type TranscriptionStatusResponse = {
	sessionMeetingId: number;
	transcriptionStatus: 'disabled' | 'enabling' | 'enabled';
};

export const sessionsApi = {
	sendCalendarEmail: (pairId: number, membershipId: number, sessionId: number, payload: CalendarMeetingPayload) =>
		httpClient.post(`sessions/send-calendar-email/${pairId}/${membershipId}/${sessionId}`, payload),
	getSessions: (pairId: number, membershipId: number) =>
		httpClient.get<Session[]>(`sessions/${pairId}/${membershipId}`),
	createSession: (pairId: number, membershipId: number, afterSessionNumber: number) =>
		httpClient.post<Session>(`sessions/create/${pairId}/${membershipId}/${afterSessionNumber}`),
	updateSession: (pairId: number, membershipId: number, id: number, payload: SessionPayload) =>
		httpClient.post<Session>(`sessions/update/${pairId}/${membershipId}/${id}`, payload),
	updateSessionFinishedState: (pairId: number, membershipId: number, id: number) =>
		httpClient.post<Session>(`sessions/update/${pairId}/${membershipId}/${id}/finished`),
	updateSessionRating: (pairId: number, membershipId: number, id: number, payload: SessionRatingPayload) =>
		httpClient.post<Session>(`sessions/update/${pairId}/${membershipId}/${id}/rating`, payload),
	updateSessionReflections: (
		pairId: number,
		membershipId: number,
		id: number,
		payload: Partial<SessionReflections>
	) => httpClient.post<Session>(`sessions/update/${pairId}/${membershipId}/${id}/reflections`, payload),
	updateSessionHomework: (
		pairId: number,
		membershipId: number,
		id: number,
		payload: Partial<SessionHomeworkPayload>
	) => httpClient.post<Session>(`sessions/update/${pairId}/${membershipId}/${id}/homework`, payload),
	deleteSession: (pairId: number, membershipId: number, sessionId: number) =>
		httpClient.delete(`sessions/${pairId}/${membershipId}/${sessionId}`),

	// videocall
	getSessionMeetingVideoCall: (uuid: string) =>
		httpClient.get<SessionMeetingVideoCallResponse>(`sessions/session-meeting/${uuid}`),
	joinToRoom: (uuid: string) => httpClient.post(`sessions/session-meeting/room/${uuid}/join`),
	leaveRoom: (uuid: string) => httpClient.delete(`sessions/session-meeting/room/${uuid}/leave`),
	startTranscription: (uuid: string, locale?: string) =>
		httpClient.post<TranscriptionStatusResponse>(`sessions/session-meeting/${uuid}/transcription`, { locale }),
	stopTranscription: (uuid: string) =>
		httpClient.put<TranscriptionStatusResponse>(`sessions/session-meeting/${uuid}/transcription`),
	getTranscription: (uuid: string) =>
		httpClient.get<TranscriptionStatusResponse>(`sessions/session-meeting/${uuid}/transcription`),
};
